import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Center, Spinner, Text } from '@chakra-ui/react';
import {
  TableOptions,
  DataTable,
  Pagination,
  Button,
  type Column,
} from '@themes/aegix';
import { useQuery } from '@tanstack/react-query';
import { usePlanningStudents } from '../../hooks/queries/useReunification';
import { getStudentContacts } from '../../services/oneInvitations.service';
import { InviteToOneDrawer } from './InviteToOneDrawer';
import type { PlanningStudent } from '../../types/ReunificationTypes';

interface StudentRow {
  [key: string]: unknown;
  id: string;
  name: string;
  school: string;
  grade: string;
  externalId: string;
  _raw: PlanningStudent;
}

function transformStudents(students: PlanningStudent[]): StudentRow[] {
  return students.map((student) => ({
    id: student.public_id,
    name: `${student.last_name}, ${student.first_name}`.trim(),
    school: student.school_name || '-',
    grade: student.grade || '-',
    externalId: student.external_reference_id || '-',
    _raw: student,
  }));
}

interface StudentsListProps {
  schoolId?: string;
}

export function StudentsList({ schoolId }: StudentsListProps) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  // Invite drawer state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteStudent, setInviteStudent] = useState<PlanningStudent | null>(
    null,
  );

  // Emergency-contacts fetch goes through React Query (ADR 102), keyed on
  // the student id, so it participates in `queryClient.invalidateQueries()`
  // from `AuthContext.setActiveOrganization`. The drawer renders an empty
  // list until the query resolves.
  const inviteStudentId = inviteStudent?.public_id;
  const { data: inviteContactsData } = useQuery({
    queryKey: ['studentContacts', inviteStudentId],
    queryFn: () => getStudentContacts(inviteStudentId!),
    enabled: !!inviteStudentId,
  });
  const inviteContacts = inviteContactsData?.contacts ?? [];

  const { data, isLoading, isFetching, error } = usePlanningStudents({
    search: searchValue || undefined,
    page: currentPage,
    page_size: pageSize,
    ordering: sortColumn
      ? `${sortDirection === 'desc' ? '-' : ''}${sortColumn}`
      : undefined,
    school: schoolId ? Number(schoolId) : undefined,
  });

  const students = data?.results ? transformStudents(data.results) : [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, pageSize, filterValues]);

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [column]: value }));
  }, []);

  const handleFilterClear = useCallback((column: string) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }, []);

  const filteredStudents = useMemo(() => {
    const activeFilters = Object.entries(filterValues).filter(([, v]) => v);
    if (activeFilters.length === 0) return students;
    return students.filter((row) =>
      activeFilters.every(([col, val]) => {
        const cellValue = String(
          (row as Record<string, unknown>)[col] ?? '',
        ).toLowerCase();
        return cellValue.includes(val.toLowerCase());
      }),
    );
  }, [students, filterValues]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((prev) =>
          prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
        );
        if (sortDirection === 'desc') setSortColumn(undefined);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const handleInviteClick = useCallback((row: StudentRow) => {
    // Setting `inviteStudent` enables the `studentContacts` query above,
    // which fetches emergency contacts in the background. Errors are
    // tolerated — the drawer still works (manual entry available).
    setInviteStudent(row._raw);
    setInviteOpen(true);
  }, []);

  const columns: Column<StudentRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('common.name', 'Name'),
        sortable: true,
        filterable: true,
        width: '220px',
      },
      {
        key: 'school',
        label: t('common.school', 'School'),
        sortable: false,
        filterable: true,
        width: '200px',
      },
      {
        key: 'grade',
        label: t('students.gradeClass', 'Grade / Class'),
        sortable: false,
        filterable: true,
        width: '200px',
      },
      {
        key: 'externalId',
        label: t('students.studentId', 'Student ID'),
        sortable: false,
        filterable: true,
        width: '160px',
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        width: '140px',
        render: (row: StudentRow) => (
          <Button
            visual="secondary"
            size="sm"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleInviteClick(row);
            }}
           data-e2e="invite-to-one-button">
            {t('students.inviteToOne', 'Invite to ONE')}
          </Button>
        ),
      },
    ],
    [handleInviteClick, t],
  );

  return (
    <>
      <TableOptions
        searchValue={searchValue}
        onSearchChange={(v) => {
          setSearchValue(v);
          setCurrentPage(1);
        }}
      />

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error
                ? error.message
                : t('students.failedLoad', 'Failed to load students')}
            </Text>
          </Center>
        ) : students.length === 0 ? (
          <Center py={12}>
            {isFetching ? (
              <Spinner size="lg" color="button.primary" />
            ) : (
              <Text color="text.secondary">{t('students.noStudentsFound', 'No students found')}</Text>
            )}
          </Center>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={filteredStudents}
              keyExtractor={(row) => row.id}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onFilterClear={handleFilterClear}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </Box>

      {inviteStudent && (
        <InviteToOneDrawer
          isOpen={inviteOpen}
          onClose={() => {
            setInviteOpen(false);
            setInviteStudent(null);
          }}
          student={{
            public_id: inviteStudent.public_id,
            first_name: inviteStudent.first_name,
            last_name: inviteStudent.last_name,
            grade: inviteStudent.grade ?? undefined,
          }}
          schoolId={schoolId ?? ''}
          emergencyContacts={inviteContacts}
        />
      )}
    </>
  );
}
